import { useEffect, useId, useMemo, useState } from 'react';
import { api } from '../api/apiClient';
import { IconKey } from '../ui/icons';

function demoClientTitle(demo) {
  const c = demo?.client || null;
  const company = String(c?.company_name || '').trim();
  const name = `${String(c?.first_name || '').trim()} ${String(c?.last_name || '').trim()}`.trim();
  return company || name || String(c?.email || '').trim() || 'Client';
}

export default function LoginPage({ onLogin, onStartDemo }) {
  const emailId = useId();
  const passwordId = useId();
  const errorId = useId();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading
  const [error, setError] = useState('');
  const [demo, setDemo] = useState({ status: 'idle', token: '', info: null, error: '' }); // idle | loading | ready | error

  const demoToken = useMemo(() => {
    try {
      const qs = new URLSearchParams(window.location.search || '');
      return String(qs.get('demo') || '').trim();
    } catch {
      return '';
    }
  }, []);

  useEffect(() => {
    if (!demoToken) return;
    let cancelled = false;
    setDemo({ status: 'loading', token: demoToken, info: null, error: '' });
    api
      .demoInfo(demoToken)
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setDemo({ status: 'error', token: demoToken, info: null, error: res.error || 'Démo introuvable' });
          return;
        }
        setDemo({ status: 'ready', token: demoToken, info: res.demo || null, error: '' });
      })
      .catch(() => {
        if (cancelled) return;
        setDemo({ status: 'error', token: demoToken, info: null, error: 'Démo introuvable' });
      });
    return () => {
      cancelled = true;
    };
  }, [demoToken]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setStatus('loading');
    try {
      const res = await onLogin({ email, password });
      if (!res.ok) setError(res.error || 'Connexion impossible');
    } finally {
      setStatus('idle');
    }
  }

  return (
    <div className="LoginShell" role="region" aria-label="Connexion">
      <div className="LoginCol LoginCol--left">
        <div className="LoginInner">
          <div className="LoginKicker">Omniscope</div>
          <h1 className="LoginTitle">Connexion</h1>

          {demo.status === 'ready' && demo.info ? (
            <div className="LoginDemo">
              <div className="LoginDemoTop">
                <span className="LoginDemoBadge">
                  <IconKey />
                  <span>Accès démo disponible</span>
                </span>
              </div>
              <button
                type="button"
                className="LoginButton LoginButton--demo"
                onClick={() => onStartDemo?.({ token: demo.token, demo: demo.info })}
              >
                Démo pour {demoClientTitle(demo.info)}
              </button>
            </div>
          ) : demo.status === 'loading' ? (
            <div className="LoginDemo">
              <div className="Muted">Chargement de la démo…</div>
            </div>
          ) : demo.status === 'error' && demoToken ? (
            <div className="Error" role="alert">
              {demo.error || 'Lien démo invalide.'}
            </div>
          ) : null}

          <form className="LoginForm" onSubmit={handleSubmit} aria-describedby={error ? errorId : undefined} autoComplete="off">
            <div className="Field">
              <label className="LoginLabel" htmlFor={emailId}>
                Email
              </label>
              <input
                id={emailId}
                name="omniscope-email"
                className="LoginInput"
                autoComplete="off"
                inputMode="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="Field">
              <label className="LoginLabel" htmlFor={passwordId}>
                Mot de passe
              </label>
              <input
                id={passwordId}
                name="omniscope-password"
                className="LoginInput"
                autoComplete="new-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {error ? (
              <div id={errorId} className="Error" role="alert">
                {error}
              </div>
            ) : null}

            <button className="LoginButton" type="submit" disabled={status === 'loading'}>
              {status === 'loading' ? 'Connexion…' : 'Se connecter'}
            </button>
          </form>
        </div>
      </div>

      <div className="LoginCol LoginCol--right" aria-hidden="true">
        <img className="LoginImage" src="/omniscope_3d.png" alt="" />
      </div>
    </div>
  );
}
